import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { scroller } from "react-scroll";

const aboutDropdown = [
  { label: "Who We Are",              section: "who-we-are",        page: null      },
  { label: "Mission & Vision",        section: "mission-vision",    page: "/about"  },
  { label: "Our Core Values",         section: "core-values",       page: "/about"  },
  // { label: "Ambassador's Message",    section: "ambassador-message",page: "/about"  },
  // { label: "President's Message",     section: "president-message", page: "/about"  },
  { label: "Board Members",           section: "board-members",     page: "/about"  },
];

const newsDropdown = [
  { label: "Annual Reports",       section: "annual-reports",       page: "/news" },
  { label: "Market insights",      section: "market-insights",      page: "/news" },
  { label: "Policy Papers",        section: "policy-papers",        page: "/news/policy-papers" },
  { label: "Press Release",        section: "press-release",        page: "/news" },
  { label: "Bilateral trade news", section: "bilateral-trade-news", page: "/news" },
  { label: "Business articles",    section: "business-articles",    page: "/news" },
  { label: "Chamber updates",      section: "chamber-updates",      page: "/news" },
];

export default function Navbar() {
  const [scrolled, setScrolled]               = useState(false);
  const [mobileOpen, setMobileOpen]           = useState(false);
  const [aboutOpen, setAboutOpen]             = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const dropdownRef                            = useRef<HTMLDivElement>(null);
  const [newsOpen, setNewsOpen]               = useState(false);
  const [mobileNewsOpen, setMobileNewsOpen]   = useState(false);
  const newsDropdownRef                        = useRef<HTMLDivElement>(null);
  const [pendingScroll, setPendingScroll]     = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const isHome   = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setAboutOpen(false);
      if (newsDropdownRef.current && !newsDropdownRef.current.contains(e.target as Node))
        setNewsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navLinks = [
    { label: "Home",           section: "home"     },
    { label: "Services",       section: "services" },
    { label: "Trade & Invest", section: "trade"    },
    { label: "Membership",        section: "membership"  },
    { label: "Events",         section: "events"   },
    { label: "News",           section: "news"     },
    { label: "Activity",       section: "activity" },
    { label: "Gallery",        section: "gallery"  },
    { label: "Contact",        section: "contact"  },
  ];

  const smoothScroll = (section: string) => {
    scroller.scrollTo(section, { duration: 1000, delay: 0, smooth: "easeInOutQuart", offset: -80 });
  };

  useEffect(() => {
    if (pendingScroll) {
      const timer = setTimeout(() => {
        smoothScroll(pendingScroll);
        setPendingScroll(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, pendingScroll]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, section: string) => {
    e.preventDefault();
    setMobileOpen(false); setAboutOpen(false); setNewsOpen(false);
    if (section === "home") {
      if (!isHome) navigate("/");
      window.scrollTo(0, 0);
      return;
    }
    if (!isHome) { setPendingScroll(section); navigate("/"); return; }
    smoothScroll(section);
  };

  const handleJoinClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMobileOpen(false);
    if (!isHome) { setPendingScroll("membership"); navigate("/"); return; }
    smoothScroll("membership");
  };

  const handleAboutItem = (item: typeof aboutDropdown[0]) => {
    setAboutOpen(false); setMobileOpen(false); setMobileAboutOpen(false);
    const targetPage = item.page || "/";
    if (location.pathname === targetPage) {
      item.section ? smoothScroll(item.section) : window.scrollTo(0, 0);
    } else {
      if (item.section) setPendingScroll(item.section);
      navigate(targetPage);
    }
  };

  const handleNewsItem = (item: typeof newsDropdown[0]) => {
    setNewsOpen(false); setMobileOpen(false); setMobileNewsOpen(false);
    const targetPage = item.page || "/";
    if (location.pathname === targetPage) {
      item.section ? smoothScroll(item.section) : window.scrollTo(0, 0);
    } else {
      if (item.section) setPendingScroll(item.section);
      navigate(targetPage);
    }
  };

  const isSolid    = !isHome || scrolled;
  const navBg      = isSolid
    ? "bg-white/95 backdrop-blur-md shadow-[0_4px_20px_rgba(15,28,53,0.05)] border-b border-[#0F1C35]/5"
    : "bg-transparent border-b border-transparent";
  const linkCls    = isSolid
    ? "text-[#3D4A62] hover:bg-[#0077b6]/10 hover:text-[#0077b6]"
    : "text-white/90 hover:bg-white/15 hover:text-white";
  const hamColor   = isSolid ? "bg-[#0F1C35]" : "bg-white";
  const chevronCls = isSolid ? "text-[#3D4A62]" : "text-white/80";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 font-sans ${navBg} ${isSolid ? "py-0" : "py-2"}`}
      >
        <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1580px] mx-auto">
          <div className="flex items-center justify-between h-16 md:h-20">

            {/* ── Logo — same image for both scrolled and not scrolled ── */}
            <a
              href="/"
              onClick={(e) => handleNavClick(e, "home")}
              className="flex items-center cursor-pointer flex-shrink-0"
            >
              <img
                src="https://i.postimg.cc/B6RH8Bcz/1000016498.jpg"
                alt="BICCI Logo"
                className="h-12 md:h-16 w-auto object-contain flex-shrink-0 transition-all duration-500"
              />
            </a>

            {/* ── Desktop nav ── */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-1.5">
              <a
                href="#home"
                onClick={(e) => handleNavClick(e, "home")}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${linkCls}`}
              >
                Home
              </a>

              {/* About dropdown */}
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setAboutOpen(!aboutOpen)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${linkCls}`}
                >
                  About
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${chevronCls} ${aboutOpen ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {aboutOpen && (
                  <div
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-[0_16px_48px_rgba(15,28,53,0.14)] border border-[#F0EDE8] overflow-hidden"
                    style={{ zIndex: 100 }}
                  >
                    <div className="px-4 pt-3.5 pb-2.5 border-b border-[#F0EDE8]">
                      <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#0077b6]">About BICCI</span>
                    </div>
                    <div className="py-2">
                      {aboutDropdown.map((item) => (
                        <button
                          key={item.label}
                          onClick={() => handleAboutItem(item)}
                          className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors duration-150 cursor-pointer group"
                          style={{ background: "transparent" }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(201,149,42,0.05)"; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                        >
                          <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#0077b6] mt-[6px] opacity-60 group-hover:opacity-100 transition-opacity" />
                          <div className="text-sm font-medium text-[#0F1C35] group-hover:text-[#0077b6] transition-colors">
                            {item.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {navLinks.filter((l) => l.section !== "home").map((link) => {
                if (link.label === "News") {
                  return (
                    <div ref={newsDropdownRef} className="relative" key="news">
                      <button
                        onClick={() => setNewsOpen(!newsOpen)}
                        className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${linkCls}`}
                      >
                        News
                        <svg
                          className={`w-3.5 h-3.5 transition-transform duration-200 ${chevronCls} ${newsOpen ? "rotate-180" : ""}`}
                          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>

                      {newsOpen && (
                        <div
                          className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-[0_16px_48px_rgba(15,28,53,0.14)] border border-[#F0EDE8] overflow-hidden"
                          style={{ zIndex: 100 }}
                        >
                          <div className="px-4 pt-3.5 pb-2.5 border-b border-[#F0EDE8]">
                            <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#0077b6]">News & Insights</span>
                          </div>
                          <div className="py-2">
                            {newsDropdown.map((item) => (
                              <button
                                key={item.label}
                                onClick={() => handleNewsItem(item)}
                                className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors duration-150 cursor-pointer group"
                                style={{ background: "transparent" }}
                                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(201,149,42,0.05)"; }}
                                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                              >
                                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#0077b6] mt-[6px] opacity-60 group-hover:opacity-100 transition-opacity" />
                                <div className="text-sm font-medium text-[#0F1C35] group-hover:text-[#0077b6] transition-colors">
                                  {item.label}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <a
                    key={link.label}
                    href={`#${link.section}`}
                    onClick={(e) => handleNavClick(e, link.section)}
                    className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${linkCls}`}
                  >
                    {link.label}
                  </a>
                );
              })}
            </nav>

            {/* ── Actions ── */}
            <div className="flex items-center gap-4">
              <a
                href="#membership"
                onClick={handleJoinClick}
                className="hidden md:inline-flex items-center gap-2 px-6 py-2.5 text-[13px] font-bold tracking-wide uppercase rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap shadow-[0_2px_12px_rgba(0, 119, 182, 1)] hover:-translate-y-[1px] hover:shadow-[0_4px_16px_rgba(0, 119, 182, 1)] bg-[#0077b6] text-white hover:bg-[#0096c7]"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Join BICCI
              </a>

              {/* Hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-end cursor-pointer"
                aria-label="Toggle menu"
              >
                <div className="flex flex-col gap-1.5 items-end">
                  <span className={`block h-[2px] rounded-full transition-all duration-300 ${hamColor} ${mobileOpen ? "w-6 rotate-45 translate-y-[8px]" : "w-6"}`} />
                  <span className={`block h-[2px] rounded-full transition-all duration-300 ${hamColor} ${mobileOpen ? "w-0 opacity-0" : "w-5"}`} />
                  <span className={`block h-[2px] rounded-full transition-all duration-300 ${hamColor} ${mobileOpen ? "w-6 -rotate-45 -translate-y-[8px]" : "w-4"}`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════
         MOBILE SIDEBAR
      ══════════════════════════════════════ */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-all duration-400 ease-in-out ${
          mobileOpen ? "opacity-100 pointer-events-auto visible" : "opacity-0 pointer-events-none invisible"
        }`}
      >
        <div
          className={`absolute inset-0 bg-[#0F1C35]/40 backdrop-blur-sm transition-opacity duration-400 ${mobileOpen ? "opacity-100" : "opacity-0"}`}
          onClick={() => setMobileOpen(false)}
        />

        <div
          className={`absolute top-0 right-0 h-full w-[320px] max-w-[85vw] bg-white shadow-2xl flex flex-col transition-transform duration-500 ease-out ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Mobile header */}
          <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-[#F8F7F3]/50">
            <a
              href="/"
              onClick={(e) => handleNavClick(e, "home")}
              className="flex items-center cursor-pointer"
            >
              <img
                src="https://i.postimg.cc/B6RH8Bcz/1000016498.jpg"
                alt="BICCI Logo"
                className="h-10 w-auto object-contain"
              />
            </a>

            <button
              onClick={() => setMobileOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100 text-[#3D4A62] hover:text-[#0077b6] hover:border-[#0077b6]/30 transition-colors cursor-pointer flex-shrink-0 ml-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mobile nav links */}
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
            <a
              href="#home"
              onClick={(e) => handleNavClick(e, "home")}
              className="group flex items-center justify-between px-4 py-3.5 text-[15px] font-semibold text-[#0F1C35] rounded-xl hover:bg-[#0077b6]/5 hover:text-[#0077b6] transition-all duration-200 cursor-pointer"
            >
              Home
              <svg className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>

            {/* About accordion */}
            <div>
              <button
                onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                className="w-full flex items-center justify-between px-4 py-3.5 text-[15px] font-semibold text-[#0F1C35] rounded-xl hover:bg-[#0077b6]/5 hover:text-[#0077b6] transition-all duration-200 cursor-pointer"
              >
                About
                <svg
                  className={`w-4 h-4 text-[#0077b6] transition-transform duration-200 ${mobileAboutOpen ? "rotate-180" : ""}`}
                  fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {mobileAboutOpen && (
                <div className="mt-1 ml-4 border-l-2 border-[#0077b6]/20 pl-4 space-y-0.5">
                  {aboutDropdown.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => handleAboutItem(item)}
                      className="w-full flex items-start gap-3 px-3 py-3 text-left rounded-xl transition-colors duration-150 cursor-pointer"
                      style={{ background: "transparent" }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(201,149,42,0.05)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                    >
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#0077b6] mt-[6px] opacity-50" />
                      <div className="text-[14px] font-medium text-[#0F1C35]">{item.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Remaining links + News accordion */}
            {navLinks.filter((l) => l.section !== "home").map((link) => {
              if (link.label === "News") {
                return (
                  <div key="mobile-news">
                    <button
                      onClick={() => setMobileNewsOpen(!mobileNewsOpen)}
                      className="w-full flex items-center justify-between px-4 py-3.5 text-[15px] font-semibold text-[#0F1C35] rounded-xl hover:bg-[#0077b6]/5 hover:text-[#0077b6] transition-all duration-200 cursor-pointer"
                    >
                      News
                      <svg
                        className={`w-4 h-4 text-[#0077b6] transition-transform duration-200 ${mobileNewsOpen ? "rotate-180" : ""}`}
                        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>

                    {mobileNewsOpen && (
                      <div className="mt-1 ml-4 border-l-2 border-[#0077b6]/20 pl-4 space-y-0.5">
                        {newsDropdown.map((item) => (
                          <button
                            key={item.label}
                            onClick={() => handleNewsItem(item)}
                            className="w-full flex items-start gap-3 px-3 py-3 text-left rounded-xl transition-colors duration-150 cursor-pointer"
                            style={{ background: "transparent" }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(201,149,42,0.05)"; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                          >
                            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#0077b6] mt-[6px] opacity-50" />
                            <div className="text-[14px] font-medium text-[#0F1C35]">{item.label}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <a
                  key={link.label}
                  href={`#${link.section}`}
                  onClick={(e) => handleNavClick(e, link.section)}
                  className="group flex items-center justify-between px-4 py-3.5 text-[15px] font-semibold text-[#0F1C35] rounded-xl hover:bg-[#0077b6]/5 hover:text-[#0077b6] transition-all duration-200 cursor-pointer"
                >
                  {link.label}
                  <svg className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              );
            })}
          </nav>

          {/* Mobile CTA */}
          <div className="flex-shrink-0 px-6 py-6 border-t border-gray-100 bg-gray-50/50">
            <a
              href="#membership"
              onClick={handleJoinClick}
              className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-[#0077b6] text-white text-[14px] font-bold tracking-wide uppercase rounded-xl hover:bg-[#0077b6] transition-colors duration-300 shadow-[0_4px_16px_rgba(201,149,42,0.25)] cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              Join BICCI
            </a>
          </div>
        </div>
      </div>
    </>
  );
}